import React, { useEffect, useState } from 'react';
import { adminFetch } from './adminApi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function AdminDocumentApi() {
  const [clients, setClients] = useState([]);
  const [offers, setOffers] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    name: '',
    enable_offer_letter: true,
    enable_certificate: true,
  });
  const [createdSecret, setCreatedSecret] = useState(null);
  const [error, setError] = useState('');
  const [subTab, setSubTab] = useState('keys'); // keys | offers | certificates

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [kRes, oRes, certRes] = await Promise.all([
        adminFetch(`${API_URL}/api/admin/document-api/clients`),
        adminFetch(`${API_URL}/api/admin/document-api/offers`),
        adminFetch(`${API_URL}/api/admin/document-api/certificates`),
      ]);
      const kData = await kRes.json();
      const oData = await oRes.json();
      const certData = await certRes.json();
      if (kData.success) setClients(kData.clients || []);
      if (oData.success) setOffers(oData.offers || []);
      if (certData.success) setCertificates(certData.certificates || []);
      if (!kData.success) setError(kData.message || 'Failed to load API clients');
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setCreatedSecret(null);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/document-api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: form.company_name.trim(),
          name: form.name.trim() || undefined,
          enable_offer_letter: form.enable_offer_letter,
          enable_certificate: form.enable_certificate,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Failed to generate API key');
        return;
      }
      setCreatedSecret({
        api_key: data.api_key,
        webhook_secret: data.webhook_secret,
        endpoints: data.endpoints,
        client: data.client,
      });
      setForm({
        company_name: '',
        name: '',
        enable_offer_letter: true,
        enable_certificate: true,
      });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to generate');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (client) => {
    try {
      await adminFetch(`${API_URL}/api/admin/document-api/clients/${client.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !client.is_active }),
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard');
    } catch {
      prompt('Copy this value:', text);
    }
  };

  const tabBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setSubTab(key)}
      style={{
        padding: '8px 16px',
        borderRadius: 8,
        border: 'none',
        fontWeight: 700,
        cursor: 'pointer',
        background: subTab === key ? '#22c55e' : '#f1f5f9',
        color: subTab === key ? '#fff' : '#334155',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h2 style={{ marginTop: 0, color: '#0f172a' }}>Document API</h2>
      <p style={{ color: '#64748b', marginBottom: 20, lineHeight: 1.5 }}>
        Create a standalone public API key for another platform. Enter a company/display name,
        generate the key, and share it. Partners call with <code>X-API-Key</code>.
        Offer letters email Accept/Decline; certificates only generate a PDF.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {tabBtn('keys', 'API Keys')}
        {tabBtn('offers', 'Offers')}
        {tabBtn('certificates', 'Certificates')}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {subTab === 'keys' && (
        <>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h3 style={{ marginTop: 0 }}>Generate API key</h3>
            <form onSubmit={handleGenerate} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 520 }}>
              <label style={{ fontWeight: 600, fontSize: 14 }}>
                Company name *
                <input
                  required
                  className="gogig-input"
                  style={{ display: 'block', width: '100%', marginTop: 6 }}
                  placeholder="e.g. Evalis / Partner Org"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                />
              </label>
              <label style={{ fontWeight: 600, fontSize: 14 }}>
                Key label (optional)
                <input
                  className="gogig-input"
                  style={{ display: 'block', width: '100%', marginTop: 6 }}
                  placeholder="e.g. Evalis production"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>Capabilities</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.enable_offer_letter}
                    onChange={(e) => setForm({ ...form, enable_offer_letter: e.target.checked })}
                  />
                  Offer letter
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.enable_certificate}
                    onChange={(e) => setForm({ ...form, enable_certificate: e.target.checked })}
                  />
                  Certificate
                </label>
              </div>
              <button
                type="submit"
                className="gogig-card-btn"
                disabled={creating || !form.company_name.trim()}
                style={{ alignSelf: 'flex-start', opacity: creating ? 0.7 : 1 }}
              >
                {creating ? 'Generating…' : 'Generate API Key'}
              </button>
            </form>
          </div>

          {createdSecret && (
            <div style={{ background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ marginTop: 0, color: '#166534' }}>Copy now — shown once</h3>
              <p style={{ fontSize: 13, color: '#166534' }}>
                Share this API key with the other platform. The full key cannot be retrieved later.
              </p>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>API Key</div>
                <code style={{ display: 'block', wordBreak: 'break-all', background: '#fff', padding: 10, borderRadius: 8, marginTop: 4 }}>
                  {createdSecret.api_key}
                </code>
                <button type="button" className="gogig-card-btn-outline" style={{ marginTop: 8 }} onClick={() => copyText(createdSecret.api_key)}>
                  Copy API Key
                </button>
              </div>
              <div style={{ fontSize: 13, color: '#334155' }}>
                <strong>Share with partner</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  <li>Header: <code>X-API-Key: {createdSecret.api_key.slice(0, 16)}…</code></li>
                  <li><code>POST {API_URL}/api/v1/documents/offers</code> — body <code>{'{ jobId, applicationId, name, email }'}</code></li>
                  <li><code>POST {API_URL}/api/v1/documents/certificates</code> — generate PDF only</li>
                </ul>
              </div>
              <button type="button" className="gogig-card-btn-outline" style={{ marginTop: 12 }} onClick={() => setCreatedSecret(null)}>
                Dismiss
              </button>
            </div>
          )}

          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #e2e8f0', fontWeight: 700 }}>Existing keys</div>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>
            ) : clients.length === 0 ? (
              <div style={{ padding: 24, color: '#64748b' }}>No API keys yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: 12 }}>Company name</th>
                    <th style={{ padding: 12 }}>Label</th>
                    <th style={{ padding: 12 }}>Key prefix</th>
                    <th style={{ padding: 12 }}>Capabilities</th>
                    <th style={{ padding: 12 }}>Status</th>
                    <th style={{ padding: 12 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: 12 }}>{c.company_name || '—'}</td>
                      <td style={{ padding: 12 }}>{c.name}</td>
                      <td style={{ padding: 12 }}><code>{c.api_key_prefix}…</code></td>
                      <td style={{ padding: 12, fontSize: 13 }}>
                        {[c.enable_offer_letter && 'Offer', c.enable_certificate && 'Certificate'].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td style={{ padding: 12 }}>
                        <span style={{ color: c.is_active ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: 12 }}>
                        <button type="button" className="gogig-card-btn-outline" onClick={() => toggleActive(c)}>
                          {c.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {subTab === 'offers' && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          {offers.length === 0 ? (
            <div style={{ padding: 24, color: '#64748b' }}>No external offers yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>Name</th>
                  <th style={{ padding: 12 }}>Email</th>
                  <th style={{ padding: 12 }}>Status</th>
                  <th style={{ padding: 12 }}>Callback</th>
                  <th style={{ padding: 12 }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={o.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: 12 }}>{o.name}</td>
                    <td style={{ padding: 12 }}>{o.email}</td>
                    <td style={{ padding: 12 }}>{o.status}</td>
                    <td style={{ padding: 12 }}>{o.callback_status}</td>
                    <td style={{ padding: 12 }}>{o.created_at ? new Date(o.created_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {subTab === 'certificates' && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          {certificates.length === 0 ? (
            <div style={{ padding: 24, color: '#64748b' }}>No external certificates yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>Name</th>
                  <th style={{ padding: 12 }}>Email</th>
                  <th style={{ padding: 12 }}>Code</th>
                  <th style={{ padding: 12 }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {certificates.map((c) => (
                  <tr key={c.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: 12 }}>{c.name}</td>
                    <td style={{ padding: 12 }}>{c.email}</td>
                    <td style={{ padding: 12 }}><code>{c.certificate_id}</code></td>
                    <td style={{ padding: 12 }}>{c.created_at ? new Date(c.created_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
